// src/components/multi_org/MultiOrgPage.tsx
// God-mode panel cross-org: tabla de orgs + drill-down con users + impersonate.
//
// Server-side enforced via apps/superadmin/auth.py:thot_admin_auth (requires
// JWT con is_superuser=true). Client-side gate via requireSuperuser flag
// del module registry + isSuperuserFromToken() en este componente.

import { useEffect, useMemo, useState } from 'react';

import {
  superadminApi,
  startImpersonation,
  stopImpersonation,
  getActiveImpersonation,
  isSuperuserFromToken,
  type OrgSummary,
  type UserSummary,
} from '@/lib/api_superadmin';

interface MultiOrgPageProps {
  addNotification: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export function MultiOrgPage({ addNotification }: MultiOrgPageProps) {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filter, setFilter] = useState('');

  const activeImpersonation = useMemo(() => getActiveImpersonation(), []);
  const isSuperuser = useMemo(() => isSuperuserFromToken(), []);

  // ── Initial load ──────────────────────────────────────────────
  useEffect(() => {
    if (!isSuperuser) return;
    setLoadingOrgs(true);
    superadminApi.listOrgs()
      .then((data) => setOrgs(data))
      .catch((err: any) => addNotification(`Error cargando orgs: ${err.message}`, 'error'))
      .finally(() => setLoadingOrgs(false));
  }, [isSuperuser, addNotification]);

  // ── Drill-down: load users when org selected ──────────────────
  useEffect(() => {
    if (!selectedOrg) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true);
    superadminApi.listUsers(selectedOrg.org_id)
      .then((data) => setUsers(data))
      .catch((err: any) => addNotification(`Error cargando users: ${err.message}`, 'error'))
      .finally(() => setLoadingUsers(false));
  }, [selectedOrg, addNotification]);

  // ── Filtered orgs (client-side search) ────────────────────────
  const filteredOrgs = useMemo(() => {
    if (!filter.trim()) return orgs;
    const f = filter.toLowerCase();
    return orgs.filter((o) =>
      o.org_id.toLowerCase().includes(f) ||
      o.name.toLowerCase().includes(f) ||
      (o.archetype || '').toLowerCase().includes(f),
    );
  }, [orgs, filter]);

  // ── Handlers ──────────────────────────────────────────────────
  const handleImpersonate = async (org: OrgSummary) => {
    if (!confirm(`¿Actuar como "${org.name}" (${org.org_id})? La sesión actual cambiará y la app recargará.`)) {
      return;
    }
    try {
      const res = await superadminApi.impersonate(org.org_id, 30);
      addNotification(`Impersonando ${org.org_id} hasta ${new Date(res.expires_at).toLocaleTimeString()}`, 'success');
      startImpersonation(res.impersonation_token, res.org_id, res.expires_at);
      // startImpersonation hace window.location.reload()
    } catch (err: any) {
      addNotification(`Impersonate falló: ${err.message}`, 'error');
    }
  };

  const handleCreateUser = async (data: { username: string; email: string; password: string; is_superuser: boolean }) => {
    if (!selectedOrg) return;
    setCreatingUser(true);
    try {
      await superadminApi.createUser(selectedOrg.org_id, data);
      addNotification(`Usuario ${data.username} creado en ${selectedOrg.org_id}`, 'success');
      setShowCreateForm(false);
      // Reload users
      const fresh = await superadminApi.listUsers(selectedOrg.org_id);
      setUsers(fresh);
    } catch (err: any) {
      addNotification(`Error creando usuario: ${err.message}`, 'error');
    } finally {
      setCreatingUser(false);
    }
  };

  // ── Gate: not superuser → permission denied ───────────────────
  if (!isSuperuser) {
    return (
      <div className="o-card">
        <h2 className="text-lg font-bold mb-2">Acceso restringido</h2>
        <p className="text-sm">Esta sección requiere permisos de superadministrador (god-mode cross-org).</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Banner de impersonation activa ────────────────────── */}
      {activeImpersonation && (
        <div className="o-card border-amber-500/40 bg-amber-50/10">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-bold text-amber-600">⚠ Impersonando: {activeImpersonation.orgId}</div>
              <div className="text-xs opacity-80">
                Expira: {new Date(activeImpersonation.expiresAt).toLocaleString()}
              </div>
            </div>
            <button
              type="button"
              onClick={() => stopImpersonation()}
              className="o-btn-ghost text-sm"
            >
              Salir de impersonation
            </button>
          </div>
        </div>
      )}

      {/* ── Header + búsqueda ──────────────────────────────────── */}
      <div className="o-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h1 className="text-xl font-bold">Multi-Org · god-mode</h1>
          <div className="text-sm opacity-70">{orgs.length} organizaciones</div>
        </div>
        <input
          type="text"
          placeholder="Buscar por org_id, nombre o archetype..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-base"
        />
      </div>

      {/* ── Layout split: tabla orgs + detail panel ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-4">
        {/* Tabla orgs */}
        <div className="o-card">
          <h2 className="font-semibold mb-3">Organizaciones</h2>
          {loadingOrgs ? (
            <div className="text-sm opacity-70">Cargando…</div>
          ) : filteredOrgs.length === 0 ? (
            <div className="text-sm opacity-70">Sin resultados.</div>
          ) : (
            <ul className="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto">
              {filteredOrgs.map((org) => (
                <li
                  key={org.org_id}
                  className={`p-3 rounded cursor-pointer transition-colors ${
                    selectedOrg?.org_id === org.org_id
                      ? 'bg-white/15 border border-white/20'
                      : 'hover:bg-white/5'
                  }`}
                  onClick={() => setSelectedOrg(org)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{org.name}</div>
                      <div className="text-xs opacity-70 truncate">
                        <code>{org.org_id}</code>
                        {org.archetype && <> · {org.archetype}</>}
                      </div>
                    </div>
                    <div className="text-xs opacity-70 shrink-0">{org.user_count} users</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail panel */}
        <div className="o-card">
          {!selectedOrg ? (
            <div className="text-sm opacity-70">Selecciona una organización para ver detalles.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="font-bold text-lg">{selectedOrg.name}</h2>
                  <div className="text-xs opacity-70">
                    <code>{selectedOrg.org_id}</code>
                    {selectedOrg.archetype && <> · {selectedOrg.archetype}</>}
                  </div>
                  {selectedOrg.last_activity_at && (
                    <div className="text-xs opacity-60 mt-1">
                      Última actividad: {new Date(selectedOrg.last_activity_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleImpersonate(selectedOrg)}
                  className="o-btn-ghost text-sm whitespace-nowrap"
                  title="Genera un token de impersonation y recarga la app actuando como esta org (TTL 30 min)"
                >
                  ▸ Actuar como esta org
                </button>
              </div>

              <div className="border-t border-white/10 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Usuarios ({users.length})</h3>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm((s) => !s)}
                    className="o-btn-ghost text-xs"
                  >
                    {showCreateForm ? '× Cancelar' : '+ Crear usuario'}
                  </button>
                </div>

                {showCreateForm && (
                  <CreateUserForm
                    onSubmit={handleCreateUser}
                    submitting={creatingUser}
                  />
                )}

                {loadingUsers ? (
                  <div className="text-sm opacity-70">Cargando usuarios…</div>
                ) : users.length === 0 ? (
                  <div className="text-sm opacity-70">Sin usuarios en esta org.</div>
                ) : (
                  <ul className="space-y-1 max-h-[calc(100vh-440px)] overflow-y-auto">
                    {users.map((u) => (
                      <li key={u.id} className="p-2 rounded hover:bg-white/5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium truncate">
                              {u.username}
                              {u.is_superuser && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600">SUPER</span>}
                              {!u.is_active && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-600">INACTIVE</span>}
                            </div>
                            <div className="text-xs opacity-70 truncate">{u.email || '—'}</div>
                          </div>
                          {u.last_login && (
                            <div className="text-xs opacity-60 shrink-0">
                              {new Date(u.last_login).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Subcomponente: form de crear usuario ─────────────────────────

interface CreateUserFormProps {
  onSubmit: (data: { username: string; email: string; password: string; is_superuser: boolean }) => void;
  submitting: boolean;
}

function CreateUserForm({ onSubmit, submitting }: CreateUserFormProps) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSuperuser, setIsSuperuser] = useState(false);

  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    onSubmit({ username, email, password, is_superuser: isSuperuser });
    setUsername('');
    setEmail('');
    setPassword('');
    setIsSuperuser(false);
  };

  return (
    <form onSubmit={handle} className="space-y-2 mb-3 p-3 rounded border border-white/10 bg-white/5">
      <input
        type="text"
        placeholder="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        className="input-base"
        required
      />
      <input
        type="email"
        placeholder="email (opcional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="input-base"
      />
      <input
        type="password"
        placeholder="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="input-base"
        required
        minLength={8}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isSuperuser}
          onChange={(e) => setIsSuperuser(e.target.checked)}
        />
        <span>Marcar como superuser (god-mode cross-org)</span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="o-btn-ghost w-full text-sm"
      >
        {submitting ? 'Creando…' : 'Crear usuario'}
      </button>
    </form>
  );
}

export default MultiOrgPage;
